/**
 * CommercialOutcomeOrdersDrawer — the drill-in behind one commercial-outcome
 * count on the Results page ("Orders placed" / "New products" /
 * "Reactivated"). Fetches the order-grain rows on open — for one
 * campaign+window, or for one landing page (open-ended, no window) — and
 * renders them as a list: date, customer + linked contacts, what was
 * ordered, value.
 *
 * "New products" and "Reactivated" are not separate endpoints — they're
 * the same order list filtered client-side to is_first_order_in_category /
 * is_reactivated_customer, so the drawer can never disagree with the count
 * it opened from.
 *
 * A per-send row opens the same drawer narrowed to the orders credited to
 * that send (attributed_step_id), again client-side over the same list.
 *
 * The average-order-value summary is likewise computed client-side over
 * whichever list is showing (no backend change needed — booking_amt is
 * already on every order) rather than a before/after comparison: just what
 * this cohort spent per order in the window, no causal claim attached.
 */

import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignCommercialOutcomeOrder,
  CommercialOutcomeWindowDays,
  LeadSourceCommercialOutcomeOrder,
  MarketingCategory,
} from "@shared-core/data/marketing-types";
import {
  describeProductScope,
  isProductScoped,
} from "@shared-core/domain/commercial-outcome-scope";
import { Badge } from "../../primitives/badge";
import {
  formatCurrencyGbp,
  formatDateOrDash,
} from "@shared-core/domain/formatters";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../primitives/sheet";

/** Whose orders the drawer lists. A campaign's are windowed and can be
 *  narrowed to the orders credited to one send (a per-send row on the
 *  Results page), filtered client-side on attributed_step_id from the same
 *  fetched list — send null shows the whole project. A landing page's are
 *  open-ended and have no sends. */
export type CommercialOutcomeOrdersSource =
  | {
      kind: "campaign";
      campaignId: string;
      windowDays: CommercialOutcomeWindowDays;
      send: { stepId: string; label: string } | null;
    }
  | { kind: "lead_source"; leadSourceId: string };

type OrderRow =
  CampaignCommercialOutcomeOrder | LeadSourceCommercialOutcomeOrder;

interface CommercialOutcomeOrdersDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: CommercialOutcomeOrdersSource;
  /** The campaign's or landing page's name, for the description line. */
  name: string;
  /** "new_category" filters to is_first_order_in_category, "reactivated" to
   *  is_reactivated_customer, both client-side — the full list is already
   *  fetched, so no second endpoint is needed. */
  filter: "all" | "new_category" | "reactivated";
  title: string;
  /** The campaign's own product_keywords (campaign-product-keyword-outcomes),
   *  passed down from the outcome row that opened this drawer — null/empty
   *  means the backend already returned an unscoped order list. */
  productKeywords: string[] | null;
  /** The campaign's or landing page's marketing_categories, shown
   *  alongside productKeywords — null/empty means unscoped by category. */
  categories?: MarketingCategory[] | null;
}

export function CommercialOutcomeOrdersDrawer({
  open,
  onOpenChange,
  source,
  name,
  filter,
  title,
  productKeywords,
  categories = null,
}: CommercialOutcomeOrdersDrawerProps) {
  const dataAdapter = useDataAdapter();
  const [orders, setOrders] = useState<OrderRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(false);
      try {
        const res: OrderRow[] =
          source.kind === "campaign"
            ? await dataAdapter.getCampaignCommercialOutcomeOrders(
                source.campaignId,
                source.windowDays,
              )
            : await dataAdapter.getLeadSourceCommercialOutcomeOrders(
                source.leadSourceId,
              );
        if (!cancelled) setOrders(res);
      } catch (e) {
        console.error(
          "marketing.results.commercial_outcome_orders.load.failed",
          e,
        );
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // The source's identity, not the object — the page builds a fresh
    // object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdapter, open, sourceKey(source)]);

  const send = source.kind === "campaign" ? source.send : null;
  const sendOrders =
    orders && send
      ? orders.filter(
          (o) =>
            "attributed_step_id" in o && o.attributed_step_id === send.stepId,
        )
      : orders;
  const scope = { productKeywords, categories };
  const isScoped = isProductScoped(scope);
  const filtered =
    sendOrders &&
    (filter === "new_category"
      ? sendOrders.filter((o) => o.is_first_order_in_category)
      : filter === "reactivated"
        ? sendOrders.filter((o) => o.is_reactivated_customer)
        : sendOrders);

  const totalValue = filtered?.reduce((sum, o) => sum + o.booking_amt, 0) ?? 0;
  const averageValue =
    filtered && filtered.length > 0 ? totalValue / filtered.length : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {title}
            {isScoped && <Badge variant="secondary">Product-scoped</Badge>}
          </SheetTitle>
          <SheetDescription>
            {name}
            {send && <> · {send.label}</>} ·{" "}
            {source.kind === "campaign"
              ? `${source.windowDays}-day window`
              : "orders since each customer submitted the form"}
            {isScoped && <> · counts: {describeProductScope(scope)}</>}
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 space-y-3 overflow-y-auto">
          {loading && (
            <div className="text-sm text-muted-foreground">Loading orders…</div>
          )}

          {error && !loading && (
            <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
              Couldn't load orders. Please try again.
            </div>
          )}

          {!loading && !error && filtered && (
            <>
              {filtered.length === 0 ? (
                <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
                  No orders to show.
                </div>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground">
                    {filtered.length} order{filtered.length === 1 ? "" : "s"} ·{" "}
                    {formatCurrencyGbp(totalValue)} total ·{" "}
                    {formatCurrencyGbp(averageValue)} average order value
                  </div>
                  <ul className="space-y-2">
                    {filtered.map((order) => (
                      <li
                        key={order.booking_key}
                        className="rounded-lg border border-border p-3 space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-foreground">
                            {order.customer_name ?? order.customer_id}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatCurrencyGbp(order.booking_amt)}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDateOrDash(order.order_booking_date)} ·{" "}
                          {order.job_type ?? order.marketing_category}
                          {order.job_no && <> · Job {order.job_no}</>}
                          {"submitted_on" in order && (
                            <>
                              {" "}
                              · submitted {formatDateOrDash(order.submitted_on)}
                            </>
                          )}
                          {order.is_first_order_in_category && (
                            <span className="ml-1.5 text-primary">
                              (new product type)
                            </span>
                          )}
                          {order.is_reactivated_customer && (
                            <span className="ml-1.5 text-primary">
                              (reactivated)
                            </span>
                          )}
                        </div>
                        {order.contacts.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            Linked contact
                            {order.contacts.length > 1 ? "s" : ""}:{" "}
                            {order.contacts
                              .map(
                                (c) =>
                                  c.name + (c.email ? ` (${c.email})` : ""),
                              )
                              .join(", ")}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function sourceKey(source: CommercialOutcomeOrdersSource): string {
  return source.kind === "campaign"
    ? `campaign:${source.campaignId}:${source.windowDays}`
    : `lead_source:${source.leadSourceId}`;
}
